<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js"
import {ref, watch} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useClaimStore} from "@/stores/Claim.js";
import ClaimFilter from "@/views/Request/Claim/Components/ClaimFilter.vue";
import axios from "axios";
import {storeToRefs} from "pinia";

export default {
    name: "ListingModel",
    props: {
        type: {
            type: String,
            required: true,
        }
    },
    components: {ClaimFilter},
    setup(props) {
        const {
            loading,
            total,
            filter,
            itemsPerPage
        } = useSharedData();

        const {getClaimParamsByType, transformFilterValues, formatedDate} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const claimStore = useClaimStore();
        const securityStore = useSecurityStore();

        const claimParams = getClaimParamsByType(props.type);

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "2%"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                value: "code",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.worker"),
                align: "left",
                value: "workerLastName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.date"),
                align: "left",
                value: "date",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("claim.target"),
                align: "left",
                value: "targetName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.designation"),
                align: "left",
                value: "name",
                sortable: true,
            },
            {
                text: $i18n.t("common.state"),
                align: "start",
                sortable: true,
                value: "validationStatus.lastState",
                width: "0%"
            },
            {
                text: $i18n.t("common.state_date"),
                align: "start",
                sortable: true,
                value: "validationStatus.lastValidationDate",
                width: "0%"
            },
            {
                text: $i18n.t("common.next_validator"),
                align: "start",
                sortable: true,
                value: "validationStatus.nextValidator",
                width: "0%"
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: (item) => item.updateOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${claimParams.rootPath}.edit`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: (item) => item.deleteOption,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${claimParams.rootPath}.delete`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `${claimParams.rootPath}.documents`, params: {id: item.id}})
                },
            },
        ];

        const {itemsToBeValidate} = storeToRefs(securityStore);

        const searchParams = ref({
            sortBy: "date",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage
        });

        function handleSearch(params) {
            loading.value = true;
            claimStore.setClaims([]);
            total.value = 0;

            filter.value.type = props.type;
            filter.value.itemsToBeValidate = itemsToBeValidate.value;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get(`/${claimParams.api}`, {params: transformFilterValues(filter.value)}).then((response) => {
                claimStore.setClaims(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value);
        }

        watch(() => [filter.value, itemsToBeValidate.value], () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});

        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});



        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
            itemsToBeValidate.value = false;
        }

        function view(item) {
            $router.push({name: `${claimParams.rootPath}.view`, params: {id: item.id}});
        }



        return {
            claimParams,
            claimStore,
            securityStore,
            headers,
            loading,
            total,
            filter,
            searchParams,
            actions,
            itemsToBeValidate,
            findAll,
            clearFilter,
            view,
            formatedDate,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="claimParams.title"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <ClaimFilter v-model="filter" :type="type"/>
            </template>

            <template v-slot:leftToolbar>
                <v-checkbox
                    v-model="itemsToBeValidate"
                    :label="$t('common.requests_need_validation')"
                    :true-value="true"
                    :false-value="false"
                    class="ml-2"
                    hide-details
                />
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="claimParams.saveOperation"
                    @press="$router.push({ name: `${claimParams.rootPath}.add` })"
                />
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="claimStore.claims"
                    :loading="loading"
                    :total="total"
                    show-validation
                    :root-path="claimParams.rootPath"
                    :module="type"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.workerLastName`]="{ item }">
                        <w-inline-text bold :text="item.workerName"/>
                    </template>
                    <template v-slot:[`item.date`]="{ item }">
                        <w-inline-text :text="formatedDate(item.date)"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        {{ item.name }}
                    </template>
                    <template v-slot:[`item.targetName`]="{ item }">
                        <w-chip color="teal" :text="item.targetName"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>

        <router-view/>
    </div>
</template>
